import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '@/api/client';
import type { Page } from '@/api/types';
import type { FullShipment, ShipmentListItem, ShipmentOrder, ShipmentPage, ShipmentV2Page } from './types';

type Q = Record<string, string | number | undefined>;

type ListState<T, R> = {
  items: T[];
  total: number;
  page: number;
  hasNext: boolean;
  /** 最近一页的完整返回（取 stats 等附加字段） */
  data?: R;
  loading: boolean;
  error?: ApiError | Error;
};

/**
 * 通用分页列表（发货、异常共用；建议提取到 src/api 或 src/lib）。
 * - append=true（手机 / iPad 卡片）：切换条件时取第一页，「加载更多」追加；
 * - append=false（表格）：页码由调用方从 URL 传入（?page=），翻页 = 改 URL。
 * 旧请求会被 abort，避免快速切换条件时的竞态。
 */
export function usePagedList<T, R extends Page<T> = Page<T>>(path: string, query: Q, pageSize: number, append: boolean, urlPage = 1) {
  const [state, setState] = useState<ListState<T, R>>({ items: [], total: 0, page: urlPage, hasNext: false, loading: true });
  const ctrl = useRef<AbortController | null>(null);
  const key = JSON.stringify(query);

  const fetchPage = useCallback(
    async (page: number, reset: boolean) => {
      ctrl.current?.abort();
      const c = new AbortController();
      ctrl.current = c;
      setState((s) => ({ ...s, loading: true, error: undefined, ...(reset ? { items: [], total: 0, data: undefined } : {}) }));
      try {
        const data = await api.get<R>(path, { ...JSON.parse(key), page, pageSize }, c.signal);
        setState((s) => ({
          items: reset || !append ? data.items : [...s.items, ...data.items],
          total: data.pagination.total,
          page: data.pagination.page,
          hasNext: data.pagination.hasNext,
          data,
          loading: false,
        }));
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        setState((s) => ({ ...s, loading: false, error: e as Error }));
      }
    },
    [path, key, pageSize, append],
  );

  const page = append ? 1 : urlPage;
  useEffect(() => {
    fetchPage(page, true);
    return () => ctrl.current?.abort();
  }, [fetchPage, page]);

  return {
    ...state,
    pageSize,
    reload: () => fetchPage(append ? 1 : state.page, true),
    loadMore: () => state.hasNext && !state.loading && fetchPage(state.page + 1, false),
  };
}

/** V2 发货批次台账：日期、仓库与未闭环异常都由服务端精确筛选。 */
export function useShipmentList(query: { keyword?: string; period?: string; warehouseId?: string; exception?: string }, pageSize: number, append: boolean, urlPage = 1) {
  return usePagedList<ShipmentListItem, ShipmentV2Page>('/api/v2/shipments/ledger', query, pageSize, append, urlPage);
}

/** 本地 V2 单笔发货：复用 V1 的权限、库存校验和事务。 */
export function createShipment(orderId: string, payload: {
  logisticsCompany: string;
  trackingNo: string;
  note?: string;
  attachmentIds?: string[];
  lines: { orderLineId: string; quantity: number; lossQuantity: number }[];
}) {
  return api.post<{ id: string; shipmentNo: string }>(`/api/v2/orders/${encodeURIComponent(orderId)}/shipments`, payload);
}

/** 只更新 V1 共用批次上的物流字段，不改数量、库存或出库状态。 */
export function updateShipmentLogistics(id: string, payload: { logisticsCompany: string; trackingNo: string; expectedLogisticsCompany: string; expectedTrackingNo: string }) {
  return api.patch<{ id: string; logisticsCompany: string; trackingNo: string }>(`/api/v2/shipments/${encodeURIComponent(id)}/logistics`, payload);
}

type DetailState = {
  item?: ShipmentListItem;
  order?: ShipmentOrder;
  shipment?: FullShipment;
  /** 订单详情取不到（403 等）时的原因：页面退回台账数据，套装无法按套合并 */
  orderError?: Error;
  loading: boolean;
  error?: Error;
};

/**
 * 批次详情。老系统没有 GET /api/shipments/:id，分两步：
 * 1) 用批次号搜台账（keyword 覆盖 shipmentNo），精确匹配拿到 orderId；从台账点进来时先显示 seed，再取最新数据；
 * 2) GET /api/orders/:orderId，从 shipments[] 找到本批 —— 这里的 lines 带 orderLine，套装才能按套合并。
 * 两个接口的可见范围都是 getOrderVisibilityWhere，看得见批次就看得见订单。
 */
export function useShipmentDetail(no: string, seed?: ShipmentListItem) {
  const [state, setState] = useState<DetailState>({ loading: true, item: seed?.shipmentNo === no ? seed : undefined });
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    const c = new AbortController();
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: undefined, orderError: undefined }));
    (async () => {
      try {
        // History state can retain a ledger seed across reloads. Always read the
        // shared V1 row so edits made by either version appear here.
        const page = await api.get<ShipmentPage>('/api/shipments', { keyword: no, pageSize: 20 }, c.signal);
        const item = page.items.find((x) => x.shipmentNo === no);
        if (!item) throw new ApiError(`没有找到批次 ${no}，可能已被删除，或当前账号看不到这张订单`, 404);
        if (!alive) return;
        setState((s) => ({ ...s, item }));
        try {
          const order = await api.get<ShipmentOrder>(`/api/orders/${item.orderId}`, undefined, c.signal);
          const shipment = order.shipments?.find((x) => x.id === item!.id);
          if (alive) setState({ item, order, shipment, loading: false });
        } catch (e) {
          if ((e as Error).name === 'AbortError') return;
          if (alive) setState({ item, loading: false, orderError: e as Error });
        }
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        if (alive) setState({ loading: false, error: e as Error });
      }
    })();
    return () => {
      alive = false;
      c.abort();
    };
  }, [no, nonce]); // eslint-disable-line react-hooks/exhaustive-deps
  return { ...state, reload: () => setNonce((n) => n + 1) };
}
